import { useState, useEffect, useCallback } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { Checkbox } from "../components/ui/checkbox";
import { 
  CheckSquare, 
  LayoutDashboard, 
  ListTodo, 
  Users, 
  UserPlus, 
  LogOut, 
  Plus, 
  Trash2,
  UserCheck,
  Mail,
  Clock,
  CheckCircle2,
  Circle,
  FolderOpen,
  Pencil,
  RefreshCw,
  HandHelping,
  Briefcase,
  Search,
  X,
  Tags,
  AlertTriangle
} from "lucide-react";

// Import extracted components
import { TasksPage } from "./admin";

const AdminDashboard = () => {
  const { user, logout, api } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [assets, setAssets] = useState([]);
  const [helpingTasks, setHelpingTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const apiCalls = [
        api.get("/tasks"),
        api.get("/employees"),
        api.get("/invitations"),
        api.get("/categories"),
        api.get("/assets"),
      ];
      
      // Fetch tasks admin is helping with if they're an asset
      if (user?.is_asset) {
        apiCalls.push(api.get("/tasks/helping"));
      }
      
      const responses = await Promise.all(apiCalls);
      setTasks(responses[0].data);
      setEmployees(responses[1].data);
      setInvitations(responses[2].data);
      setCategories(responses[3].data);
      setAssets(responses[4].data);
      if (user?.is_asset && responses[5]) {
        setHelpingTasks(responses[5].data);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  }, [api, user?.is_asset]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    logout();
    navigate("/login");
    toast.success("Logged out successfully");
  };

  // Build navigation items dynamically
  const navItems = [
    { path: "/admin", icon: LayoutDashboard, label: "Overview", exact: true },
    { path: "/admin/tasks", icon: ListTodo, label: "Responsibilities" },
    { path: "/admin/employees", icon: Users, label: "Future Owners" },
    { path: "/admin/assets", icon: HandHelping, label: "Assets" },
    { path: "/admin/categories", icon: Tags, label: "Categories" },
    { path: "/admin/assign", icon: UserCheck, label: "Assign" },
    { path: "/admin/assignments", icon: CheckCircle2, label: "Assignments" },
  ];
  
  // Add "Helping With" nav item if admin is an asset
  if (user?.is_asset) {
    navItems.push({ path: "/admin/helping", icon: Briefcase, label: "My Helping" });
  }

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar */}
      <aside className="sidebar w-64 bg-white border-r border-border/50 flex flex-col" data-testid="admin-sidebar">
        <div className="p-6">
          <Link to="/admin" className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer" data-testid="logo-home-link">
            <img src="/images/logo.png" alt="Future Owners Logo" className="w-12 h-12 object-contain" />
            <span className="text-xl font-bold text-primary" style={{ fontFamily: 'Outfit, sans-serif' }}>Future Owners</span>
          </Link>
        </div>
        
        <nav className="flex-1 px-4">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-all duration-200 ${
                isActive(item.path, item.exact)
                  ? "bg-primary text-white"
                  : "text-muted-foreground hover:bg-secondary hover:text-primary"
              }`}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-border/50">
          <div className="flex items-center gap-3 px-4 py-2 mb-4">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-primary font-semibold">{user?.name?.[0]?.toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground">Admin</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : (
          <Routes>
            <Route index element={
              <OverviewPage 
                tasks={tasks} 
                employees={employees} 
                invitations={invitations}
                assets={assets}
              />
            } />
            <Route path="tasks" element={
              <TasksPage 
                tasks={tasks} 
                categories={categories}
                assets={assets}
                employees={employees}
                onTaskCreated={fetchData}
                onTaskDeleted={fetchData}
                api={api}
              />
            } />
            <Route path="employees" element={
              <EmployeesPage 
                employees={employees}
                invitations={invitations}
                tasks={tasks}
                onInviteSent={fetchData}
                onInviteDeleted={fetchData}
                api={api}
              />
            } />
            <Route path="assets" element={
              <AssetsPage 
                assets={assets}
                employees={employees}
                tasks={tasks}
                onAssetCreated={fetchData}
                api={api}
              />
            } />
            <Route path="categories" element={
              <CategoriesPage 
                tasks={tasks}
                onCategoryChange={fetchData}
                api={api}
              />
            } />
            <Route path="assign" element={
              <AssignPage 
                tasks={tasks}
                employees={employees}
                assets={assets}
                onAssign={fetchData}
                api={api}
              />
            } />
            <Route path="assignments" element={
              <AssignmentsPage 
                tasks={tasks}
                employees={employees}
                assets={assets}
                onAssetChange={fetchData}
              />
            } />
            {user?.is_asset && (
              <Route path="helping" element={
                <AdminHelpingWith 
                  helpingTasks={helpingTasks}
                  employees={employees}
                  assets={assets}
                  api={api}
                />
              } />
            )}
          </Routes>
        )}
      </main>
    </div>
  );
};

// Overview Page Component
const OverviewPage = ({ tasks, employees, invitations, assets }) => {
  const navigate = useNavigate();
  const openTasks = tasks.filter(t => t.status === "open").length;
  const assignedTasks = tasks.filter(t => t.status === "assigned").length;
  const pendingInvites = invitations.filter(i => i.status === "pending").length;
  
  // Separate Future Owners and Admins
  const futureOwners = employees.filter(e => e.role === "employee" && !e.is_asset);
  const admins = employees.filter(e => e.role === "admin");

  const stats = [
    { label: "Total Responsibilities", value: tasks.length, icon: ListTodo, color: "bg-primary/10 text-primary", link: "/admin/tasks" },
    { label: "Open", value: openTasks, icon: Circle, color: "bg-green-100 text-green-600", link: "/admin/tasks" },
    { label: "Assigned", value: assignedTasks, icon: CheckCircle2, color: "bg-purple-100 text-purple-600", link: "/admin/assignments" },
    { label: "Future Owners", value: futureOwners.length, icon: Users, color: "bg-blue-100 text-blue-600", link: "/admin/employees" },
    { label: "Assets", value: assets.length, icon: HandHelping, color: "bg-orange-100 text-orange-600", link: "/admin/assets" },
    { label: "Pending Invites", value: pendingInvites, icon: Mail, color: "bg-amber-100 text-amber-600", link: "/admin/employees" },
  ];

  return (
    <div className="animate-in" data-testid="overview-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Dashboard
        </h1>
        <p className="text-muted-foreground">Welcome back! Here's your responsibility overview.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-8">
        {stats.map((stat, index) => (
          <Card 
            key={index} 
            className="border-border/50 shadow-soft hover-lift cursor-pointer transition-all hover:border-primary/50" 
            data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
            onClick={() => navigate(stat.link)}
          >
            <CardContent className="p-6">
              <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center mb-4`}>
                <stat.icon className="w-6 h-6" />
              </div>
              <p className="text-3xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{stat.value}</p>
              <p className="text-sm text-muted-foreground">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Responsibilities */}
      <div className="mb-6">
        <Card 
          className="border-border/50 shadow-soft cursor-pointer hover:border-primary/50 transition-all"
          onClick={() => navigate("/admin/tasks")}
        >
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <ListTodo className="w-5 h-5 text-primary" />
              Recent Responsibilities
            </CardTitle>
          </CardHeader>
          <CardContent>
            {tasks.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No responsibilities yet. Create your first one!</p>
            ) : (
              <div className="space-y-3">
                {tasks.slice(0, 5).map((task) => (
                  <div key={task.id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
                    <div className="flex items-center gap-3">
                      <div className={`status-dot ${task.status}`}></div>
                      <span className="font-medium">{task.title}</span>
                    </div>
                    <Badge variant="secondary" className="text-xs">{task.category}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Future Owners and Admins in separate sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Future Owners Section */}
        <Card 
          className="border-border/50 shadow-soft cursor-pointer hover:border-primary/50 transition-all"
          onClick={() => navigate("/admin/employees")}
        >
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Users className="w-5 h-5 text-blue-600" />
              Future Owners ({futureOwners.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {futureOwners.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No future owners yet. Invite your team!</p>
            ) : (
              <div className="space-y-3">
                {futureOwners.slice(0, 5).map((emp) => (
                  <div key={emp.id} className="flex items-center gap-3 p-3 rounded-lg bg-blue-50">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-blue-600 font-semibold">{emp.name[0].toUpperCase()}</span>
                    </div>
                    <div>
                      <p className="font-medium">{emp.name}</p>
                      <p className="text-xs text-muted-foreground">{emp.email}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Admins Section */}
        <Card className="border-border/50 shadow-soft">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <UserCheck className="w-5 h-5 text-purple-600" />
              Administrators ({admins.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {admins.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No administrators.</p>
            ) : (
              <div className="space-y-3">
                {admins.slice(0, 5).map((admin) => (
                  <div key={admin.id} className="flex items-center gap-3 p-3 rounded-lg bg-purple-50">
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                      <span className="text-purple-600 font-semibold">{admin.name[0].toUpperCase()}</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{admin.name}</p>
                      <p className="text-xs text-muted-foreground">{admin.email}</p>
                    </div>
                    <Badge className="bg-purple-100 text-purple-700">Admin</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
// Employees Page Component
const EmployeesPage = ({ employees, invitations, tasks, onInviteSent, onInviteDeleted, api }) => {
  const [invite, setInvite] = useState({ email: "", name: "", role: "employee", is_asset: false });
  const [isSending, setIsSending] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resendingId, setResendingId] = useState(null);

  // Filter to show only Future Owners (role=employee, not assets)
  const futureOwnersOnly = employees.filter(emp => emp.role === "employee" && !emp.is_asset);

  const handleSendInvite = async () => {
    if (!invite.email || !invite.name) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsSending(true);
    try {
      await api.post("/invitations", invite);
      toast.success("Invitation sent successfully!");
      setInvite({ email: "", name: "", role: "employee", is_asset: false });
      setDialogOpen(false);
      onInviteSent();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invitation");
    } finally {
      setIsSending(false);
    }
  };

  const handleDeleteInvite = async (inviteId) => {
    try {
      await api.delete(`/invitations/${inviteId}`);
      toast.success("Invitation deleted");
      onInviteDeleted();
    } catch (error) {
      toast.error("Failed to delete invitation");
    }
  };

  const handleResendInvite = async (inviteId) => {
    setResendingId(inviteId);
    try {
      await api.post(`/invitations/${inviteId}/resend`);
      toast.success("Invitation resent successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend invitation");
    } finally {
      setResendingId(null);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to remove ${userName}? This will unassign all their responsibilities.`)) {
      return;
    }
    try {
      await api.delete(`/employees/${userId}`);
      toast.success("User removed successfully");
      onInviteDeleted(); // This refreshes the data
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove user");
    }
  };

  return (
    <div className="animate-in" data-testid="employees-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Future Owners
          </h1>
          <p className="text-muted-foreground">Manage your future owners and send invitations.</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full px-6" data-testid="invite-employee-button">
              <UserPlus className="w-5 h-5 mr-2" />
              Invite Member
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Invite Team Member</DialogTitle>
              <DialogDescription>Send an email invitation to join your team.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  placeholder="John Doe"
                  value={invite.name}
                  onChange={(e) => setInvite({ ...invite, name: e.target.value })}
                  data-testid="invite-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="john@example.com"
                  value={invite.email}
                  onChange={(e) => setInvite({ ...invite, email: e.target.value })}
                  data-testid="invite-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select
                  value={invite.role}
                  onValueChange={(value) => setInvite({ ...invite, role: value })}
                >
                  <SelectTrigger data-testid="invite-role-select">
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">Future Owner</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2 pt-2">
                <Checkbox 
                  id="is_asset" 
                  checked={invite.is_asset}
                  onCheckedChange={(checked) => setInvite({ ...invite, is_asset: checked })}
                  data-testid="invite-is-asset-checkbox"
                />
                <Label htmlFor="is_asset" className="text-sm font-normal cursor-pointer">
                  Can help others with responsibilities (Asset)
                </Label>
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline" className="rounded-full">Cancel</Button>
              </DialogClose>
              <Button
                onClick={handleSendInvite}
                disabled={isSending}
                className="rounded-full"
                data-testid="send-invite-button"
              >
                {isSending ? "Sending..." : "Send Invitation"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Active Employees */}
        <Card className="border-border/50 shadow-soft">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Users className="w-5 h-5 text-primary" />
              Active Future Owners ({futureOwnersOnly.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {futureOwnersOnly.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No active future owners yet. Users marked as Assets appear on the Assets page.</p>
            ) : (
              <ScrollArea className="h-[400px]">
                <div className="space-y-3">
                  {futureOwnersOnly.map((emp) => (
                    <div key={emp.id} className="flex items-center gap-3 p-4 rounded-lg bg-secondary/30" data-testid={`employee-${emp.id}`}>
                      <div className="w-12 h-12 rounded-full flex items-center justify-center bg-primary/10">
                        <span className="text-primary font-semibold text-lg">{emp.name[0].toUpperCase()}</span>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{emp.name}</p>
                        <p className="text-sm text-muted-foreground">{emp.email}</p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <Badge variant="secondary" className={emp.role === "admin" ? "bg-purple-100 text-purple-600" : "bg-blue-100 text-blue-600"}>
                            {emp.role === "admin" ? "Admin" : "Future Owner"}
                          </Badge>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                        onClick={() => handleDeleteUser(emp.id, emp.name)}
                        data-testid={`delete-user-${emp.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        {/* Pending Invitations */}
        <Card className="border-border/50 shadow-soft">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Mail className="w-5 h-5 text-primary" />
              Pending Invitations ({invitations.filter(i => i.status === "pending").length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {invitations.filter(i => i.status === "pending").length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No pending invitations.</p>
            ) : (
              <ScrollArea className="h-[400px]">
                <div className="space-y-3">
                  {invitations.filter(i => i.status === "pending").map((inv) => (
                    <div key={inv.id} className="flex items-center gap-3 p-4 rounded-lg bg-secondary/30" data-testid={`invitation-${inv.id}`}>
                      <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                        <Clock className="w-6 h-6 text-amber-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{inv.name}</p>
                        <p className="text-sm text-muted-foreground">{inv.email}</p>
                      </div>
                      <Badge variant="secondary" className={inv.role === "admin" ? "bg-purple-100 text-purple-600" : "bg-blue-100 text-blue-600"}>
                        {inv.role === "admin" ? "Admin" : "Future Owner"}
                      </Badge>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-primary hover:bg-primary/10"
                          onClick={() => handleResendInvite(inv.id)}
                          disabled={resendingId === inv.id}
                          data-testid={`resend-invite-${inv.id}`}
                          title="Resend invitation"
                        >
                          <RefreshCw className={`w-4 h-4 ${resendingId === inv.id ? 'animate-spin' : ''}`} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                          onClick={() => handleDeleteInvite(inv.id)}
                          data-testid={`delete-invite-${inv.id}`}
                          title="Delete invitation"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Categories Page Component - Manage responsibility categories
const CategoriesPage = ({ tasks, onCategoryChange, api }) => {
  const [categoryStats, setCategoryStats] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchCategoryStats = useCallback(async () => {
    try {
      const response = await api.get("/categories/stats");
      setCategoryStats(response.data);
    } catch (error) {
      toast.error("Failed to load category stats");
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchCategoryStats();
  }, [fetchCategoryStats]);

  const handleDeleteCategory = async (categoryName) => {
    setIsDeleting(true);
    try {
      await api.delete(`/categories/${encodeURIComponent(categoryName)}`);
      toast.success(`Category "${categoryName}" removed`);
      setDeleteConfirm(null);
      fetchCategoryStats();
      onCategoryChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete category");
    } finally {
      setIsDeleting(false);
    }
  };

  const unusedCategories = categoryStats.filter(cat => cat.count === 0);
  const usedCategories = categoryStats.filter(cat => cat.count > 0);

  return (
    <div className="animate-in" data-testid="categories-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Categories
          </h1>
          <p className="text-muted-foreground">Manage responsibility categories and clean up unused ones.</p>
        </div>
        <Button 
          variant="outline" 
          className="rounded-full"
          onClick={fetchCategoryStats}
          data-testid="refresh-categories-button"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : categoryStats.length === 0 ? (
        <Card className="border-dashed border-border/50">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Tags className="w-16 h-16 text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              No Categories Yet
            </h3>
            <p className="text-muted-foreground text-center">
              Categories are created when you add responsibilities.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-border/50">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Categories</p>
                    <p className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {categoryStats.length}
                    </p>
                  </div>
                  <Tags className="w-10 h-10 text-primary/20" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-border/50">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">In Use</p>
                    <p className="text-3xl font-bold text-green-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {usedCategories.length}
                    </p>
                  </div>
                  <CheckCircle2 className="w-10 h-10 text-green-600/20" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-border/50">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Unused</p>
                    <p className="text-3xl font-bold text-orange-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {unusedCategories.length}
                    </p>
                  </div>
                  <AlertTriangle className="w-10 h-10 text-orange-600/20" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Categories List */}
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>All Categories</CardTitle>
              <CardDescription>Categories are derived from your responsibilities. Delete unused ones to keep your list clean.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {categoryStats.map((cat) => (
                  <div 
                    key={cat.category}
                    className={`flex items-center justify-between p-4 rounded-lg border ${
                      cat.count === 0 
                        ? 'bg-orange-50/50 border-orange-200' 
                        : 'bg-white border-border/50'
                    }`}
                    data-testid={`category-row-${cat.category}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        cat.count === 0 ? 'bg-orange-100' : 'bg-primary/10'
                      }`}>
                        <Tags className={`w-5 h-5 ${cat.count === 0 ? 'text-orange-600' : 'text-primary'}`} />
                      </div>
                      <div>
                        <p className="font-medium">{cat.category}</p>
                        <p className={`text-sm ${cat.count === 0 ? 'text-orange-600' : 'text-muted-foreground'}`}>
                          {cat.count === 0 ? 'No responsibilities' : `${cat.count} responsibilit${cat.count === 1 ? 'y' : 'ies'}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={cat.count === 0 ? "outline" : "secondary"} className={cat.count === 0 ? 'border-orange-300 text-orange-600' : ''}>
                        {cat.count}
                      </Badge>
                      {cat.count === 0 && (
                        <Dialog open={deleteConfirm === cat.category} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
                          <DialogTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-orange-600 hover:text-orange-700 hover:bg-orange-100"
                              onClick={() => setDeleteConfirm(cat.category)}
                              data-testid={`delete-category-${cat.category}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Delete Category</DialogTitle>
                              <DialogDescription>
                                Are you sure you want to delete the category "<strong>{cat.category}</strong>"? This action cannot be undone.
                              </DialogDescription>
                            </DialogHeader>
                            <DialogFooter>
                              <DialogClose asChild>
                                <Button variant="outline">Cancel</Button>
                              </DialogClose>
                              <Button 
                                variant="destructive"
                                onClick={() => handleDeleteCategory(cat.category)}
                                disabled={isDeleting}
                                data-testid="confirm-delete-category"
                              >
                                {isDeleting ? "Deleting..." : "Delete"}
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Assets Page Component - Users who can help with responsibilities
const AssetsPage = ({ assets, employees, tasks, onAssetCreated, api }) => {
  const [isUpdating, setIsUpdating] = useState(null);

  const handleToggleAsset = async (userId, currentIsAsset) => {
    setIsUpdating(userId);
    try {
      await api.put(`/users/${userId}/asset-status`, { 
        is_asset: !currentIsAsset
      });
      toast.success(currentIsAsset ? "User is no longer an asset" : "User is now an asset");
      onAssetCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    } finally {
      setIsUpdating(null);
    }
  };

  // Combine assets (users with is_asset=true) and show all users for toggling
  const allUsers = employees || [];
  const assetUsers = assets || [];

  return (
    <div className="animate-in" data-testid="assets-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Assets (Helpers)
        </h1>
        <p className="text-muted-foreground">Mark users who can help Future Owners with their responsibilities.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Current Assets */}
        <Card className="border-border/50 shadow-soft">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <HandHelping className="w-5 h-5 text-orange-500" />
              Active Assets ({assetUsers.length})
            </CardTitle>
            <CardDescription>Users who can help with any responsibility</CardDescription>
          </CardHeader>
          <CardContent>
            {assetUsers.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No users are marked as assets yet.</p>
            ) : (
              <ScrollArea className="h-[500px]">
                <div className="space-y-3">
                  {assetUsers.map((user) => (
                    <div key={user.id} className="flex items-center gap-3 p-4 rounded-lg bg-orange-50" data-testid={`asset-card-${user.id}`}>
                      <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                        <HandHelping className="w-6 h-6 text-orange-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold">{user.name}</h3>
                        <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                        <Badge variant="secondary" className={user.role === "admin" ? "bg-purple-100 text-purple-600" : "bg-blue-100 text-blue-600"}>
                          {user.role === "admin" ? "Admin" : "Future Owner"}
                        </Badge>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full border-orange-300 text-orange-700 hover:bg-orange-100"
                        onClick={() => handleToggleAsset(user.id, true)}
                        disabled={isUpdating === user.id}
                      >
                        {isUpdating === user.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                        ) : (
                          "Remove"
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        {/* All Users - Toggle Asset Status */}
        <Card className="border-border/50 shadow-soft">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Users className="w-5 h-5 text-primary" />
              All Users ({allUsers.length})
            </CardTitle>
            <CardDescription>Click to make any user an asset</CardDescription>
          </CardHeader>
          <CardContent>
            {allUsers.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No users found.</p>
            ) : (
              <ScrollArea className="h-[500px]">
                <div className="space-y-3">
                  {allUsers.map((user) => (
                    <div key={user.id} className="flex items-center gap-3 p-4 rounded-lg bg-secondary/30" data-testid={`user-toggle-${user.id}`}>
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${user.is_asset ? 'bg-orange-100' : 'bg-primary/10'}`}>
                        {user.is_asset ? (
                          <HandHelping className="w-6 h-6 text-orange-600" />
                        ) : (
                          <span className="text-primary font-semibold text-lg">{user.name[0].toUpperCase()}</span>
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{user.name}</p>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                        <div className="flex gap-2 mt-1">
                          <Badge variant="secondary" className={user.role === "admin" ? "bg-purple-100 text-purple-600" : "bg-blue-100 text-blue-600"}>
                            {user.role === "admin" ? "Admin" : "Future Owner"}
                          </Badge>
                          {user.is_asset && (
                            <Badge className="bg-orange-100 text-orange-700">Asset</Badge>
                          )}
                        </div>
                      </div>
                      <Button
                        variant={user.is_asset ? "default" : "outline"}
                        size="sm"
                        className={`rounded-full ${user.is_asset ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
                        onClick={() => handleToggleAsset(user.id, user.is_asset)}
                        disabled={isUpdating === user.id}
                        data-testid={`toggle-asset-${user.id}`}
                      >
                        {isUpdating === user.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                        ) : user.is_asset ? (
                          "Remove Asset"
                        ) : (
                          "Make Asset"
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Assign Tasks Page Component
const AssignPage = ({ tasks, employees, assets, onAssign, api }) => {
  const [selectedTask, setSelectedTask] = useState(null);
  const [isAssigning, setIsAssigning] = useState(false);

  const openTasks = tasks.filter(t => t.status === "open" && t.interested_employees.length > 0);
  const assignedTasks = tasks.filter(t => t.status === "assigned");

  const handleAssign = async (taskId, employeeId) => {
    setIsAssigning(true);
    try {
      await api.post("/tasks/assign", { task_id: taskId, employee_id: employeeId });
      toast.success("Responsibility assigned successfully!");
      setSelectedTask(null);
      onAssign();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign responsibility");
    } finally {
      setIsAssigning(false);
    }
  };

  const handleUnassign = async (taskId) => {
    try {
      await api.post(`/tasks/${taskId}/unassign`);
      toast.success("Responsibility unassigned");
      onAssign();
    } catch (error) {
      toast.error("Failed to unassign responsibility");
    }
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    return emp?.name || "Unknown";
  };

  return (
    <div className="animate-in" data-testid="assign-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Assign Responsibilities
        </h1>
        <p className="text-muted-foreground">Review interested future owners and assign responsibilities.</p>
      </div>

      <div className="assignment-split">
        {/* Left Panel - Tasks with Interest */}
        <Card className="border-border/50 shadow-soft h-fit lg:h-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <ListTodo className="w-5 h-5 text-primary" />
              Responsibilities with Interest ({openTasks.length})
            </CardTitle>
            <CardDescription>Click a responsibility to see interested future owners</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[calc(100vh-400px)] lg:h-[calc(100vh-350px)]">
              {openTasks.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No responsibilities with interested future owners.</p>
              ) : (
                <div className="space-y-3">
                  {openTasks.map((task) => (
                    <div
                      key={task.id}
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        selectedTask?.id === task.id
                          ? "border-primary bg-primary/5"
                          : "border-border/50 hover:border-primary/50 bg-secondary/30"
                      }`}
                      onClick={() => setSelectedTask(task)}
                      data-testid={`assign-task-${task.id}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="secondary" className="text-xs">{task.category}</Badge>
                        <span className="text-xs text-muted-foreground">
                          {task.interested_employees.length} interested
                        </span>
                      </div>
                      <h4 className="font-semibold">{task.title}</h4>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Right Panel - Details & Candidates */}
        <div className="space-y-6">
          {selectedTask ? (
            <Card className="border-border/50 shadow-soft" data-testid="selected-task-details">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <Badge variant="secondary">{selectedTask.category}</Badge>
                  <div className="flex items-center gap-2">
                    <div className="status-dot open"></div>
                    <span className="text-sm text-muted-foreground">Open</span>
                  </div>
                </div>
                <CardTitle className="text-2xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {selectedTask.title}
                </CardTitle>
                {selectedTask.description && (
                  <p className="text-muted-foreground">{selectedTask.description}</p>
                )}
                <CardDescription>
                  {selectedTask.interested_employees.length} future owner(s) interested
                </CardDescription>
              </CardHeader>
              <CardContent>
                <h4 className="font-semibold mb-4">Interested Future Owners</h4>
                <div className="space-y-3">
                  {selectedTask.interested_employees.map((empId) => {
                    const emp = employees.find(e => e.id === empId);
                    if (!emp) return null;
                    return (
                      <div key={empId} className="flex items-center justify-between p-4 rounded-lg bg-secondary/30" data-testid={`candidate-${empId}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-primary font-semibold text-lg">{emp.name[0].toUpperCase()}</span>
                          </div>
                          <div>
                            <p className="font-medium">{emp.name}</p>
                            <p className="text-sm text-muted-foreground">{emp.email}</p>
                          </div>
                        </div>
                        <Button
                          onClick={() => handleAssign(selectedTask.id, empId)}
                          disabled={isAssigning}
                          className="rounded-full"
                          data-testid={`assign-to-${empId}`}
                        >
                          <UserCheck className="w-4 h-4 mr-2" />
                          Assign
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-border/50 shadow-soft">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mb-4">
                  <UserCheck className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Select a Responsibility</h3>
                <p className="text-muted-foreground text-center">Choose a task from the left to view interested future owners.</p>
              </CardContent>
            </Card>
          )}

          {/* Assigned Tasks */}
          <Card className="border-border/50 shadow-soft">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <CheckCircle2 className="w-5 h-5 text-primary" />
                Assigned Tasks ({assignedTasks.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {assignedTasks.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">No assigned responsibilities yet.</p>
              ) : (
                <ScrollArea className="h-[200px]">
                  <div className="space-y-3">
                    {assignedTasks.map((task) => (
                      <div key={task.id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30" data-testid={`assigned-task-${task.id}`}>
                        <div>
                          <p className="font-medium">{task.title}</p>
                          <p className="text-sm text-muted-foreground">
                            Assigned to: {getEmployeeName(task.assigned_to)}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => handleUnassign(task.id)}
                          data-testid={`unassign-${task.id}`}
                        >
                          Unassign
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Assignments Page Component - View assignments by person with asset management
const AssignmentsPage = ({ tasks, employees, assets, onAssetChange }) => {
  const { api } = useAuth();

  const assignedTasks = tasks.filter(t => t.status === "assigned");
  
  // Group tasks by assigned employee (Future Owners only, not admins)
  const assignmentsByPerson = employees
    .filter(emp => emp.role === "employee")
    .map(emp => {
      const empTasks = assignedTasks.filter(t => t.assigned_to === emp.id);
      return {
        employee: emp,
        tasks: empTasks
      };
    }).filter(item => item.tasks.length > 0);

  // Get unassigned future owners (exclude assets and admins)
  const employeesWithoutAssignments = employees.filter(emp => 
    emp.role === "employee" && !emp.is_asset && !assignedTasks.some(t => t.assigned_to === emp.id)
  );

  const getAssetInfo = (assetId) => {
    return assets.find(a => a.id === assetId);
  };

  const handleAssignAsset = async (taskId, assetUserId) => {
    if (!api) return;
    try {
      await api.post(`/tasks/${taskId}/assets`, { task_id: taskId, asset_user_id: assetUserId });
      toast.success("Asset assigned to responsibility!");
      if (onAssetChange) onAssetChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign asset");
    }
  };

  const handleRemoveAsset = async (taskId, assetUserId) => {
    if (!api) return;
    try {
      await api.delete(`/tasks/${taskId}/assets/${assetUserId}`);
      toast.success("Asset removed from responsibility");
      if (onAssetChange) onAssetChange();
    } catch (error) {
      toast.error("Failed to remove asset");
    }
  };

  return (
    <div className="animate-in" data-testid="assignments-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Assignments Overview
        </h1>
        <p className="text-muted-foreground">See which responsibilities are assigned to each future owner and manage helpers.</p>
      </div>

      {assignmentsByPerson.length === 0 ? (
        <Card className="border-border/50 shadow-soft">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>No Assignments Yet</h3>
            <p className="text-muted-foreground text-center">Assign responsibilities to future owners to see them here.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {assignmentsByPerson.map(({ employee, tasks: empTasks }) => (
            <Card key={employee.id} className="border-border/50 shadow-soft" data-testid={`assignments-${employee.id}`}>
              <CardHeader>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-primary font-bold text-xl">{employee.name[0].toUpperCase()}</span>
                  </div>
                  <div>
                    <CardTitle className="text-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {employee.name}
                    </CardTitle>
                    <CardDescription>{employee.email}</CardDescription>
                  </div>
                  <Badge className="ml-auto bg-primary/10 text-primary">
                    {empTasks.length} {empTasks.length === 1 ? 'responsibility' : 'responsibilities'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {empTasks.map(task => (
                    <div key={task.id} className="p-4 rounded-lg bg-secondary/30 border border-border/50">
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="secondary" className="text-xs">{task.category}</Badge>
                        <div className="flex items-center gap-1">
                          <div className="status-dot assigned"></div>
                          <span className="text-xs text-muted-foreground">Assigned</span>
                        </div>
                      </div>
                      <h4 className="font-semibold mb-1">{task.title}</h4>
                      {task.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{task.description}</p>
                      )}
                      
                      {/* Assets Section */}
                      <Separator className="my-3" />
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                            <HandHelping className="w-3 h-3" /> Helpers
                          </span>
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-6 px-2 text-xs"
                                data-testid={`add-asset-to-${task.id}`}
                              >
                                <Plus className="w-3 h-3 mr-1" /> Add
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-md">
                              <DialogHeader>
                                <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Assign Helper</DialogTitle>
                                <DialogDescription>Select an asset to help with "{task.title}"</DialogDescription>
                              </DialogHeader>
                              <div className="py-4">
                                {assets.filter(a => !task.assigned_assets?.includes(a.id)).length === 0 ? (
                                  <p className="text-muted-foreground text-center py-4">All assets are already assigned to this task.</p>
                                ) : (
                                  <ScrollArea className="h-[300px]">
                                    <div className="space-y-2">
                                      {assets.filter(a => !task.assigned_assets?.includes(a.id)).map(asset => (
                                        <div key={asset.id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors">
                                          <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                                              <HandHelping className="w-5 h-5 text-orange-600" />
                                            </div>
                                            <div>
                                              <p className="font-medium text-sm">{asset.name}</p>
                                              {asset.specialty && (
                                                <p className="text-xs text-muted-foreground">{asset.specialty}</p>
                                              )}
                                            </div>
                                          </div>
                                          <Button 
                                            size="sm" 
                                            className="rounded-full"
                                            onClick={() => handleAssignAsset(task.id, asset.id)}
                                            data-testid={`assign-asset-${asset.id}-to-${task.id}`}
                                          >
                                            Assign
                                          </Button>
                                        </div>
                                      ))}
                                    </div>
                                  </ScrollArea>
                                )}
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                        
                        {task.assigned_assets && task.assigned_assets.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {task.assigned_assets.map(assetId => {
                              const asset = getAssetInfo(assetId);
                              if (!asset) return null;
                              return (
                                <div 
                                  key={assetId} 
                                  className="flex items-center gap-1 px-2 py-1 rounded-full bg-orange-50 text-orange-700 text-xs group"
                                  data-testid={`task-asset-${assetId}`}
                                >
                                  <HandHelping className="w-3 h-3" />
                                  <span>{asset.name}</span>
                                  <button
                                    onClick={() => handleRemoveAsset(task.id, assetId)}
                                    className="ml-1 opacity-0 group-hover:opacity-100 hover:text-red-600 transition-opacity"
                                    data-testid={`remove-asset-${assetId}-from-${task.id}`}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground italic">No helpers assigned</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}

          {employeesWithoutAssignments.length > 0 && (
            <Card className="border-border/50 shadow-soft border-dashed">
              <CardHeader>
                <CardTitle className="text-lg text-muted-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Future Owners Without Assignments ({employeesWithoutAssignments.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {employeesWithoutAssignments.map(emp => (
                    <div key={emp.id} className="flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/50">
                      <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                        <span className="text-muted-foreground font-medium text-sm">{emp.name[0].toUpperCase()}</span>
                      </div>
                      <span className="text-sm text-muted-foreground">{emp.name}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

// Admin Helping With Component - Tasks the admin is helping with as an asset
const AdminHelpingWith = ({ helpingTasks, employees, assets, api }) => {
  const navigate = useNavigate();

  const getEmployeeName = (employeeId) => {
    const emp = employees.find(e => e.id === employeeId);
    return emp?.name || "Unknown";
  };

  const getAssetInfo = (assetId) => {
    return assets.find(a => a.id === assetId);
  };

  return (
    <div className="animate-in" data-testid="admin-helping-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          My Helping Assignments
        </h1>
        <p className="text-muted-foreground">Responsibilities you've been assigned to help with as an asset.</p>
      </div>

      {helpingTasks.length === 0 ? (
        <Card className="border-border/50 shadow-soft">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mb-4">
              <HandHelping className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Not Helping With Any Tasks Yet</h3>
            <p className="text-muted-foreground text-center">When you're assigned to help with responsibilities, they'll appear here.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {helpingTasks.map((task) => (
            <Card 
              key={task.id} 
              className="border-border/50 shadow-soft hover-lift cursor-pointer hover:border-orange-300 transition-colors" 
              data-testid={`helping-task-${task.id}`}
              onClick={() => navigate(`/responsibility/${task.id}`)}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <Badge variant="secondary">{task.category}</Badge>
                  <div className="flex items-center gap-1">
                    <HandHelping className="w-4 h-4 text-orange-500" />
                    <span className="text-xs text-orange-600 font-medium">Helping</span>
                  </div>
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{task.title}</h3>
                {task.description && (
                  <p className="text-sm text-muted-foreground mb-3">{task.description}</p>
                )}
                
                {/* Show who is assigned */}
                {task.assigned_to && (
                  <div className="text-sm text-muted-foreground mb-3">
                    <span className="font-medium">Assigned to:</span> {getEmployeeName(task.assigned_to)}
                  </div>
                )}
                
                {/* Show other helpers if any */}
                {task.assigned_assets && task.assigned_assets.length > 1 && (
                  <div className="mt-3 pt-3 border-t border-border/50">
                    <p className="text-xs font-medium text-muted-foreground mb-2">Other Helpers</p>
                    <div className="flex flex-wrap gap-2">
                      {task.assigned_assets.map(assetId => {
                        const asset = getAssetInfo(assetId);
                        if (!asset) return null;
                        return (
                          <div 
                            key={assetId}
                            className="flex items-center gap-1 px-2 py-1 rounded-full bg-orange-50 text-orange-700 text-xs"
                          >
                            <HandHelping className="w-3 h-3" />
                            <span>{asset.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
